import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import { getOrderSituationLabel } from '../../../../enums/order-enum'
import { formatDateToInput } from '../../../../utils/formatDateToInput'
import { transformFiltersForUrl } from '../../../../utils/transformFiltersForUrl'

/**
 * Formata um valor para exibição, trocando vazios por "-".
 */
const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  return value
}

/**
 * Formata datas vindas da API (ISO, ISO com hora ou ISO com timezone UTC)
 * para exibição no padrão DD/MM/AAAA.
 */
const displayDate = (value) => {
  if (!value) return '-'

  const normalized = String(value).split('T')[0].split(' ')[0]
  const [year, month, day] = normalized.split('-')

  if (year && month && day) {
    return `${day}/${month}/${year}`
  }

  return formatDateToInput(value) || '-'
}

/**
 * Verifica se o item da requisição é um insumo (produto) ou equipamento.
 */
const isProductItem = (item) => Boolean(item.product_id || item.product)

/** Kits are identified by their explicit kit type or extra-equipment relation. */
const isKitEquipmentItem = (item) =>
  Boolean(
    item.type?.is_kit ||
      item.equipment?.type?.is_kit ||
      item.extra_equipments?.length
  )

/**
 * Nome principal do item do contrato.
 */
const getContractItemAlias = (item) => {
  const alias = item.contract_item?.alias || item.alias
  const description = item.contract_item?.description

  if (alias) return alias
  if (description) return description
  if (isProductItem(item)) return 'Insumos'
  if (isKitEquipmentItem(item)) return 'Kits'
  return '-'
}

/**
 * Descrição complementar do item do contrato, quando diferente do nome.
 */
const getContractItemDescription = (item) => {
  const alias = item.contract_item?.alias || item.alias
  const description = item.contract_item?.description

  if (!alias || !description || description === alias) return null

  return description
}

/**
 * Monta a descrição do equipamento ou insumo.
 */
const getItemDescription = (item) => {
  if (item.equipment) {
    return (
      [item.equipment.number, item.equipment.name]
        .filter(Boolean)
        .join(' - ') || '-'
    )
  }

  if (item.product) {
    return item.product.name || item.alias || '-'
  }

  return item.alias || '-'
}

/**
 * Quantidade solicitada do item.
 */
const getRequestedQuantity = (item) =>
  isProductItem(item)
    ? item.quantity_product ?? item.quantity ?? 0
    : item.quantity ?? 0

/**
 * Quantidade pendente do item.
 *
 * Na API, fulfilled_quantity/fulfilled_quantity_product representam o saldo
 * que ainda falta atender, não a quantidade já atendida.
 */
const getPendingQuantity = (item) =>
  isProductItem(item)
    ? item.fulfilled_quantity_product ?? item.fulfilled_quantity ?? 0
    : item.fulfilled_quantity ?? 0

/**
 * Quantidade já atendida do item.
 */
const getAttendedQuantity = (item) => {
  const requested = Number(getRequestedQuantity(item)) || 0
  const pending = Number(getPendingQuantity(item)) || 0

  return Math.max(0, requested - pending)
}

const infoLabelSx = {
  display: 'block',
  color: '#6E707E',
  fontWeight: 700,
  fontSize: '0.68rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}

const tableHeaderSx = {
  fontWeight: 700,
  backgroundColor: '#eef1f4',
  color: '#4a4a4a',
  whiteSpace: 'nowrap',
}

/**
 * Campo de informação do cabeçalho do painel.
 */
const InfoItem = ({ label, value, fullWidth = false }) => (
  <Box sx={{ gridColumn: fullWidth ? '1 / -1' : 'auto', minWidth: 0 }}>
    <Typography variant="caption" sx={infoLabelSx}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: '#333',
        whiteSpace: fullWidth ? 'pre-wrap' : 'normal',
        wordBreak: 'break-word',
      }}>
      {displayValue(value)}
    </Typography>
  </Box>
)

/**
 * Painel de detalhes exibido ao expandir uma requisição na listagem.
 *
 * A busca pelos detalhes só acontece quando este componente é montado, ou
 * seja, quando o usuário clica na seta da requisição. Assim a listagem
 * principal não carrega os relacionamentos de todas as requisições.
 */
const ServiceOrderDetailsPanel = ({ orderId }) => {
  const navigate = useNavigate()
  const { get: baseServiceGet, getByColumn: baseServiceGetByColumn } =
    useBaseService(navigate)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['serviceOrderDetailsPanel', orderId],
    queryFn: async () => {
      const [orderResponse, itemsResponse] = await Promise.all([
        baseServiceGetByColumn(entities.serviceOrders.getById(orderId)),
        baseServiceGet(entities.serviceOrders.items(orderId), {
          filters: transformFiltersForUrl({ deleted_at: false }),
          page: 1,
          perPage: 200,
          sort: { column: 'id', order: 'asc' },
        }),
      ])

      return {
        order: orderResponse.result,
        items: itemsResponse.result?.data ?? [],
        totalItems: itemsResponse.result?.total ?? 0,
      }
    },
    enabled: !!orderId,
    staleTime: 0,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (isError || !data?.order) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Não foi possível carregar os detalhes desta requisição.
      </Alert>
    )
  }

  const { order, items = [], totalItems = 0 } = data

  const contractLabel =
    [order.contract?.number, order.contract?.name].filter(Boolean).join(' - ') ||
    '-'

  const customerLabel =
    [
      order.recipient_customer?.id,
      order.recipient_customer?.alias || order.recipient_customer?.name,
    ]
      .filter(Boolean)
      .join(' - ') || '-'

  const destinationLabel =
    order.destination_address?.alias || order.destination_order?.name || '-'

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #e0e0e0',
        borderBottom: '1px solid #e0e0e0',
      }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}>
        <InfoItem label="Cliente destinatário" value={customerLabel} />
        <InfoItem label="Contrato" value={contractLabel} />
        <InfoItem
          label="Tipo de movimento"
          value={order.movement_type?.name}
        />
        <InfoItem label="Status" value={order.status?.name} />
        <InfoItem
          label="Situação"
          value={getOrderSituationLabel(order.situation)}
        />
        <InfoItem label="Destino" value={destinationLabel} />
        <InfoItem
          label="Meio de transporte"
          value={order.mode_transport?.name}
        />
        <InfoItem label="Prazo final" value={displayDate(order.deadline)} />
        <InfoItem label="Criado em" value={displayDate(order.created_at)} />
        <InfoItem label="Criado por" value={order.user?.name} />
        <InfoItem
          label="Responsável pela decisão"
          value={order.changed_by?.name}
        />
        <InfoItem
          label="Organização"
          value={order.contract?.organization?.alias}
        />
        <InfoItem label="Detalhes" value={order.details} fullWidth />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: '#6E707E',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}>
          Equipamentos e insumos
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {totalItems || items.length}{' '}
          {(totalItems || items.length) === 1 ? 'item' : 'itens'}
        </Typography>
      </Box>

      {items.length === 0 ? (
        <Alert severity="info">Nenhum item encontrado nesta requisição.</Alert>
      ) : (
        <>
          <TableContainer
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              maxHeight: 320,
            }}>
            <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderSx}>Item do contrato</TableCell>
                  <TableCell sx={tableHeaderSx}>Detalhes</TableCell>
                  <TableCell sx={tableHeaderSx} align="right">
                    Quantidade requisitada
                  </TableCell>
                  <TableCell sx={tableHeaderSx} align="right">
                    Quantidade atendida
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const rawDetails = item.details
                    ? String(item.details).trim()
                    : ''
                  const details = rawDetails && rawDetails !== '-' ? rawDetails : null
                  const itemDescription = getItemDescription(item)
                  const contractItemDescription = getContractItemDescription(item)
                  const secondaryDetails =
                    details &&
                    itemDescription !== '-' &&
                    itemDescription !== details
                      ? itemDescription
                      : null

                  return (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getContractItemAlias(item)}
                        </Typography>
                        {contractItemDescription && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}>
                            {contractItemDescription}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2">
                          {details || itemDescription}
                        </Typography>
                        {secondaryDetails && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}>
                            {secondaryDetails}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {getRequestedQuantity(item)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {getAttendedQuantity(item)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalItems > items.length && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}>
              Mostrando {items.length} de {totalItems} itens.
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}

export default ServiceOrderDetailsPanel
